import { isAxiosError } from 'axios';
import { axiosInstance } from '@/shared/lib/axios';
import type {
  AddOnRulesResponse,
  Provider,
  ProviderProfileImageReviewStatus,
  Service,
  ServiceAddOnRule,
  Schedule,
} from '@/shared/types';
import type { ShopPaymentPolicy } from '@/shared/types/domain/payment-capability.types';
import type { AddOnBehavior } from '@/shared/types/enums';
import { logger } from '@/shared/utils/logger';

const BASE_URL = '/api/v1/providers';

export const provider = {
  /**
   * Get the current authenticated provider
   * Fetches provider data based on the JWT token
   * @param token - Optional access token to pass directly (bypasses axios interceptor timing issues)
   * @returns Provider data or null if the user is not a provider (204 No Content)
   */
  getCurrent: async (token?: string | null): Promise<Provider | null> => {
    const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    const response = await axiosInstance.get(`${BASE_URL}/current`, config);
    // 204 No Content means user is not a provider (e.g., client user)
    if (response.status === 204 || !response.data) {
      return null;
    }
    return response.data;
  },

  getProviders: async (shopId: string): Promise<Provider[]> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/shop/${shopId}`,
    );
    return response.data;
  },

  getProvider: async (id: string): Promise<Provider> => {
    const response = await axiosInstance.get(`${BASE_URL}/${id}`);
    return response.data;
  },

  updateProvider: async (
    id: string,
    updates: Partial<Provider>,
  ): Promise<Provider> => {
    const response = await axiosInstance.put(
      `${BASE_URL}/${id}`,
      updates,
    );
    return response.data;
  },

  /**
   * Update provider profile (used by onboarding and the My Profile screen).
   * Email is owned by Auth0 and intentionally not part of this payload.
   */
  updateProfile: async (
    providerId: string,
    updates: {
      name?: string;
      phoneNumber?: string;
      bio?: string;
      providerType?: string;
      pictureUrl?: string;
      timezone?: string;
      address?: string;
    },
  ): Promise<Provider> => {
    const response = await axiosInstance.put(
      `${BASE_URL}/${providerId}/profile`,
      updates,
    );
    return response.data;
  },

  /**
   * Persist the provider's preferred app/communication locale.
   *
   * Best-effort: errors are swallowed and `null` is returned so a failed sync
   * (offline, transient 5xx, mid-onboarding race) never disrupts the local
   * language switch, which has already been applied via i18next + localStorage.
   */
  updatePreferredLocale: async (
    providerId: string,
    preferredLocale: string,
  ): Promise<string | null> => {
    try {
      const response = await axiosInstance.patch<{ preferredLocale: string }>(
        `${BASE_URL}/${providerId}/preferred-locale`,
        { preferredLocale },
      );
      return response.data?.preferredLocale ?? null;
    } catch (error) {
      logger.warn('Failed to sync provider preferred locale', { error });
      return null;
    }
  },

  getProfileImageReviewStatus: async (
    providerId: string,
  ): Promise<ProviderProfileImageReviewStatus> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/${providerId}/profile/image-review-status`,
    );
    return response.data;
  },

  updatePaymentPolicy: async (
    providerId: string,
    paymentPolicy: ShopPaymentPolicy,
  ): Promise<Provider> => {
    const response = await axiosInstance.put(
      `${BASE_URL}/${providerId}/payment-policy`,
      { paymentPolicy },
    );
    return response.data;
  },

  deleteProvider: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/${id}`);
  },

  // Service operations
  getServices: async (providerId: string): Promise<Service[]> => {
    logger.debug('Fetching services for provider:', providerId);
    const response = await axiosInstance.get(
      `${BASE_URL}/${providerId}/services`,
    );
    // Ensure we always return an array
    const services = Array.isArray(response.data) ? response.data : [];
    logger.debug('Services received:', services.length);
    return services;
  },

  /**
   * Shop-scoped provider services read for shop-workspace callers.
   * Backend filters results to the given shop and also authorizes the actor
   * against that shop. Used when the UI is in a specific shop context.
   */
  getServicesForShop: async (
    providerId: string,
    shopId: string,
  ): Promise<Service[]> => {
    logger.debug('Fetching shop-scoped services:', { providerId, shopId });
    const response = await axiosInstance.get(
      `${BASE_URL}/${providerId}/services`,
      { params: { shopId } },
    );
    const services = Array.isArray(response.data) ? response.data : [];
    logger.debug('Shop-scoped services received:', services.length);
    return services;
  },

  // Scope-aware service categories read for provider dashboard flows.
  // Applies strict vertical scoping on the backend using shop vertical +
  // requested profession.
  getServiceCategories: async (
    providerId: string,
    context?: { shopId?: string | null; profession?: string | null; includeInactive?: boolean },
  ) => {
    const params: Record<string, string> = {};
    if (context?.shopId) params.shopId = context.shopId;
    if (context?.profession) params.profession = context.profession;
    if (context?.includeInactive) params.includeInactive = 'true';
    const response = await axiosInstance.get(
      `${BASE_URL}/${providerId}/service-categories`,
      { params: Object.keys(params).length > 0 ? params : undefined },
    );
    return Array.isArray(response.data) ? response.data : [];
  },

  // Public service operations (for unauthenticated booking pages)
  getPublicServices: async (providerId: string): Promise<Service[]> => {
    logger.debug('Fetching public services for provider:', providerId);
    const response = await axiosInstance.get(
      `${BASE_URL}/${providerId}/services/public`,
    );
    const services = Array.isArray(response.data) ? response.data : [];
    logger.debug('Public services received:', services.length);
    return services;
  },

  addService: async (
    providerId: string,
    service: Omit<Service, 'id'> & { profession?: string | null },
  ): Promise<Service> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/${providerId}/services`,
      service,
    );
    return response.data;
  },

  updateService: async (
    providerId: string,
    serviceId: string,
    updates: Partial<Service> & { profession?: string | null },
    shopId?: string | null,
  ): Promise<Service> => {
    const config = shopId ? { params: { shopId } } : undefined;
    const response = await axiosInstance.put(
      `${BASE_URL}/${providerId}/services/${serviceId}`,
      updates,
      config,
    );
    return response.data;
  },

  deleteService: async (
    providerId: string,
    serviceId: string,
    shopId?: string | null,
  ): Promise<void> => {
    const config = shopId ? { params: { shopId } } : undefined;
    await axiosInstance.delete(
      `${BASE_URL}/${providerId}/services/${serviceId}`,
      config,
    );
  },

  getServiceById: async (serviceId: string): Promise<Service> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/services/${serviceId}`,
    );
    return response.data;
  },

  // Add-on rule operations
  getAddOnRules: async (providerId: string): Promise<ServiceAddOnRule[]> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/${providerId}/add-on-rules`,
    );
    return Array.isArray(response.data) ? response.data : [];
  },

  getAddOnRulesForBaseService: async (
    providerId: string,
    baseServiceId: string,
  ): Promise<AddOnRulesResponse> => {
    let response;
    try {
      response = await axiosInstance.get(
        `${BASE_URL}/${providerId}/services/${baseServiceId}/add-on-rules`,
      );
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        logger.debug(
          { providerId, baseServiceId },
          'No add-on rules context found for base service',
        );
        return { rules: [], addOnRulesOptional: false };
      }
      throw error;
    }

    // Support both envelope { rules, addOnRulesOptional } and legacy array responses
    if (response.data && 'rules' in response.data) {
      return response.data as AddOnRulesResponse;
    }
    // Backward compat: legacy array response from older backends.
    const legacyRules = Array.isArray(response.data) ? response.data : [];
    return {
      rules: legacyRules
        .filter((r: ServiceAddOnRule) => r.addOnService)
        .map((r: ServiceAddOnRule) => ({
          ruleId: r.id,
          isSynthetic: false,
          baseServiceId: r.baseServiceId,
          addOnServiceId: r.addOnServiceId,
          behavior: r.behavior,
          addOnService: {
            id: r.addOnService!.id,
            name: r.addOnService!.name,
            price: r.addOnService!.price,
            duration: r.addOnService!.duration,
            imageUrl: r.addOnService!.imageUrl ?? null,
            serviceKind: r.addOnService!.serviceKind,
          },
        })),
      addOnRulesOptional: false,
    };
  },

  upsertAddOnRule: async (
    providerId: string,
    rule: { baseServiceId: string; addOnServiceId: string; behavior: AddOnBehavior },
  ): Promise<ServiceAddOnRule> => {
    const response = await axiosInstance.put(
      `${BASE_URL}/${providerId}/add-on-rules`,
      rule,
    );
    return response.data;
  },

  bulkUpsertAddOnRules: async (
    providerId: string,
    rules: { baseServiceId: string; addOnServiceId: string; behavior: AddOnBehavior }[],
  ): Promise<ServiceAddOnRule[]> => {
    const response = await axiosInstance.put(
      `${BASE_URL}/${providerId}/add-on-rules/bulk`,
      { rules },
    );
    return Array.isArray(response.data) ? response.data : [];
  },

  deleteAddOnRule: async (providerId: string, ruleId: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/${providerId}/add-on-rules/${ruleId}`);
  },

  // Schedule operations
  /**
   * Get provider's schedule
   * Returns null if no schedule exists - this is expected for new providers
   */
  getProviderSchedule: async (providerId: string): Promise<Schedule | null> => {
    const response = await axiosInstance.get(
      `/api/v1/provider-schedules/provider/${providerId}`,
    );
    return response.data;
  },

  getScheduleById: async (scheduleId: string): Promise<Schedule> => {
    const response = await axiosInstance.get(
      `/api/v1/provider-schedules/${scheduleId}`,
    );
    return response.data;
  },

  createSchedule: async (
    providerId: string,
    schedule: {
      workingDays: string[];
      workHours: Record<string, { start: string; end: string }[]>;
      breakTimes?: Record<string, { start: string; end: string }[]>;
    },
  ): Promise<Schedule> => {
    const response = await axiosInstance.post(
      `/api/v1/provider-schedules/${providerId}`,
      schedule,
    );
    return response.data;
  },

  updateSchedule: async (
    providerId: string,
    scheduleId: string,
    updates: {
      workingDays?: string[];
      workHours?: Record<string, { start: string; end: string }[]>;
      breakTimes?: Record<string, { start: string; end: string }[]>;
    },
  ): Promise<Schedule> => {
    const response = await axiosInstance.put(
      `/api/v1/provider-schedules/${scheduleId}`,
      updates,
    );
    return response.data;
  },

  getProviderAvailability: async (
    providerId: string,
    date: string,
    serviceIds: string[],
    shopId?: string,
  ): Promise<string[]> => {
    // Build the query parameters
    const params = new URLSearchParams();
    params.append('date', date);
    if (shopId) {
      params.append('shopId', shopId);
    }

    // Handle multiple service IDs
    if (serviceIds && serviceIds.length > 0) {
      serviceIds.forEach(id => params.append('serviceIds', id));
    }

    const response = await axiosInstance.get(
      `/api/v1/provider-availability/${providerId}?${params.toString()}`,
    );
    logger.debug('response', response.data);

    // Handle both old format (array) and new format (object with timeSlots)
    if (Array.isArray(response.data)) {
      return response.data;
    }
    return response.data.timeSlots || [];
  },
};
